
import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    Flex,
    Badge,
    Icon,
    Center,
    Spinner,
    useColorModeValue,
    SimpleGrid,
    Alert,
    AlertIcon,
    Spacer,
    Card,
    CardBody,
    IconButton,
    ButtonGroup
} from '@chakra-ui/react';
import { AddIcon, RepeatIcon, EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { MdContentCopy, MdEdit, MdFileDownload, MdLock, MdCheckCircle, MdAutoAwesome } from 'react-icons/md';
import { TransactionsExplorer } from '../components/dashboard/TransactionsExplorer';
import { useUser } from '../Context/userContext';
import { useSnapshotStats } from '../hooks/useSnapshotStats';
import { useAccounts } from '../hooks/useAccounts';
import { AccountSelector } from '../components/common/AccountSelector';
import { usePivotState } from '../hooks/usePivotState';
import { FilterBar } from '../components/transactions-v2';
import {
    pivotModeToGroupBy,
    viewModeToExplorer,
    areSameAccountSelection,
    type AccountSelectionPhase,
} from '../components/transactions-v2/pivotMappings';

export const SnapshotPage: React.FC = () => {
    const { selectedTenant, setSelectedAccountId } = useUser();
    const [hasLoadedSnapshot, setHasLoadedSnapshot] = useState(false);
    const [explorerResetKey, setExplorerResetKey] = useState(0);
    const [accountSelectionPhase, setAccountSelectionPhase] = useState<AccountSelectionPhase>('idle');
    const [isExplorerLoading, setIsExplorerLoading] = useState(false);
    const [pivotState, pivotActions] = usePivotState();
    const aiOpenRef = useRef<(() => void) | undefined>();
    const exportRef = useRef<(() => void) | undefined>();
    // Bulk-edit panel lives in this page (sibling of FilterBar) so it stays
    // visible while scrolling the inner table. We expose triggers for it via refs.
    const bulkEditOpenRef = useRef<(() => void) | undefined>();
    const clearSelectionRef = useRef<(() => void) | undefined>();
    const [bulkEditPanel, setBulkEditPanel] = useState<{ visible: boolean; count: number }>({
        visible: false,
        count: 0,
    });
    // Ensures we auto-fit the date range to the snapshot data only once per load,
    // so it never overrides a date range the user has manually chosen afterwards.
    const didInitDateRangeRef = useRef(false);
    const {
        stats,
        loading: snapshotLoading,
        initSnapshot,
        resetSnapshot: resetSnapshotBase,
        refreshSnapshot: refreshSnapshotBase,
        fetchStats,
    } = useSnapshotStats(selectedTenant?.id);
    const { accounts } = useAccounts();

    const resetSnapshot = async () => {
        // Allow the date range to auto-fit again to the freshly rebuilt snapshot.
        didInitDateRangeRef.current = false;
        await resetSnapshotBase();
        setExplorerResetKey(k => k + 1);
    };

    const refreshSnapshot = async () => {
        // New transactions extend the data span — let it auto-fit again.
        didInitDateRangeRef.current = false;
        await refreshSnapshotBase();
        setExplorerResetKey(k => k + 1);
    };

    const {
        startDate,
        endDate,
        accountIds,
        viewMode,
        pivotMode,
        pivotOrientation,
        searchQuery,
        searchMode,
        filterMode,
        flowFilter,
    } = pivotState;

    useEffect(() => {
        if (accountSelectionPhase === 'pending' && isExplorerLoading) {
            setAccountSelectionPhase('loading');
        } else if (accountSelectionPhase === 'loading' && !isExplorerLoading) {
            setAccountSelectionPhase('idle');
        }
    }, [accountSelectionPhase, isExplorerLoading]);

    const handleAccountsChange = (ids: string[]) => {
        const currentIds = accountIds ?? [];
        if (!areSameAccountSelection(ids, currentIds)) {
            setAccountSelectionPhase('pending');
        }
        pivotActions.setAccountIds(ids);
        if (ids.length === 1) setSelectedAccountId(ids[0]);
        else setSelectedAccountId('');
    };

    // Initial fetch to check status
    useEffect(() => {
        let active = true;

        const loadSnapshotStatus = async () => {
            if (!selectedTenant?.id) {
                if (active) setHasLoadedSnapshot(true);
                return;
            }

            if (active) setHasLoadedSnapshot(false);
            try {
                await fetchStats();
            } finally {
                if (active) setHasLoadedSnapshot(true);
            }
        };

        loadSnapshotStatus();

        return () => {
            active = false;
        };
    }, [selectedTenant?.id, fetchStats]);

    // Re-allow date-range auto-fit whenever the tenant changes.
    useEffect(() => {
        didInitDateRangeRef.current = false;
    }, [selectedTenant?.id]);

    // Auto-fit the visible date range to the snapshot's actual data span on first load.
    // The default pivot range is the current month, but a snapshot is a frozen dataset
    // that usually spans many months. Without this, selecting an account whose
    // transactions all fall outside the current month shows "No transactions in this
    // period" even though the account has plenty of data — the reported bug.
    // (Previously computed by looping every row client-side; now a single
    // aggregate query on the backend — see useSnapshotStats.)
    useEffect(() => {
        if (didInitDateRangeRef.current) return;
        if (!stats.minDate || !stats.maxDate) return;

        didInitDateRangeRef.current = true;
        pivotActions.setDateRange(stats.minDate, stats.maxDate);
    // pivotActions.setDateRange is stable; intentionally keyed on the stats dates only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stats.minDate, stats.maxDate]);

    const hasSnapshot = stats.total > 0;

    // Connected accounts that have transactions but are NOT represented in the
    // snapshot — e.g. an account connected after the snapshot was created.
    // Selecting one shows "No transactions"; a refresh adds them (edits preserved).
    const missingAccounts = React.useMemo(() => {
        if (!hasSnapshot) return [];
        const inSnapshot = new Set(stats.accountIdsPresent);
        return accounts.filter(
            (a) => (a.transactionCount ?? 0) > 0 && !inSnapshot.has(a.id),
        );
    }, [accounts, stats.accountIdsPresent, hasSnapshot]);

    // Colors
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const groupBy = pivotModeToGroupBy(pivotMode);
    const explorerViewMode = viewModeToExplorer(viewMode, filterMode);
    // Only the plain table view gets the contained-scroll/sticky-header
    // treatment — see TenantTransactionsV2Page and TransactionTable's
    // containedScroll prop doc.
    const isTableView = explorerViewMode === 'table';

    // Edit tracking — both counts come straight from the stats aggregate query.
    const editedCount = stats.editedCount;
    const originalCount = stats.originalCount;
    // Status filter state — lifted from TransactionsExplorer so the filter UI can
    // live in FilterBar's secondary row (alongside search/flow/filter-by).
    const [editFilter, setEditFilter] = useState<'all' | 'edited' | 'original'>('all');

    return (
        <Box flex="1" minH={0} display="flex" flexDirection="column" w="full" overflow="hidden">

            {/* Minimal title + account picker — only before a scenario exists.
                Once a scenario is active, the title/status live as compact
                chips in FilterBar's row 1 (see `actions` below) instead of a
                dedicated ~90px card, which is most of what this page's chrome
                used to cost (see docs/plans/UI_LAYOUT_DENSITY_PLAN.md §H5). */}
            {!hasSnapshot && (
                <HStack
                    justify="space-between"
                    align="center"
                    px={4}
                    py={3}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                    flexShrink={0}
                    wrap="wrap"
                    gap={3}
                >
                    <Box>
                        <Heading size="sm" color="text.primary">Scenario Editor</Heading>
                        <Text fontSize="xs" color="text.secondary">
                            Create and manage safe scenarios for data adjustment.
                        </Text>
                    </Box>
                    <AccountSelector
                        values={accountIds}
                        onChangeMulti={handleAccountsChange}
                        multiSelect
                        compact
                        size="sm"
                    />
                </HStack>
            )}

            {/* Out-of-date scenario banner: connected accounts missing from the snapshot */}
            {missingAccounts.length > 0 && (
                <Alert status="warning" flexShrink={0} alignItems="center">
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="bold" color="text.primary">
                            {missingAccounts.length} connected account{missingAccounts.length === 1 ? '' : 's'} not in this scenario yet
                        </Text>
                        <Text fontSize="sm" color="text.secondary">
                            {missingAccounts.map((a) => a.name).join(', ')} — selecting {missingAccounts.length === 1 ? 'it' : 'them'} shows no transactions. Refresh to include {missingAccounts.length === 1 ? 'it' : 'them'}; your edits are preserved.
                        </Text>
                    </Box>
                    <Spacer />
                    <Button
                        leftIcon={<RepeatIcon />}
                        colorScheme="orange"
                        size="sm"
                        onClick={refreshSnapshot}
                        isLoading={snapshotLoading}
                        flexShrink={0}
                    >
                        Refresh Scenario
                    </Button>
                </Alert>
            )}

            {/* Content */}
            {!hasLoadedSnapshot && selectedTenant?.id ? (
                <Center flex="1" minH={0}>
                    <VStack spacing={3}>
                        <Spinner size="lg" />
                        <Text color="text.secondary">Loading scenario...</Text>
                    </VStack>
                </Center>
            ) : hasSnapshot ? (
                <>
                    <FilterBar
                        startDate={startDate}
                        endDate={endDate}
                        onRangeSelect={pivotActions.setDateRange}
                        onPreviousPeriod={pivotActions.goToPreviousPeriod}
                        onNextPeriod={pivotActions.goToNextPeriod}
                        selectedAccountIds={accountIds}
                        onAccountsChange={handleAccountsChange}
                        isAccountSelectionLoading={accountSelectionPhase !== 'idle'}
                        accountAdornment={
                            <HStack spacing={2} px={2} py={1} bg="bg.muted" borderRadius="md" flexShrink={0}>
                                <Icon as={MdCheckCircle} color="accent.primary" boxSize={4} />
                                <Badge colorScheme="green" fontSize="0.65rem">Active</Badge>
                                <Text fontSize="xs" color="text.primary" fontWeight="medium" whiteSpace="nowrap">
                                    {stats.total.toLocaleString()} Transactions
                                </Text>
                                {editedCount > 0 && (
                                    <Badge colorScheme="orange" variant="solid" fontSize="0.65rem">
                                        {editedCount} edited
                                    </Badge>
                                )}
                            </HStack>
                        }
                        viewMode={viewMode}
                        onViewModeChange={pivotActions.setViewMode}
                        pivotMode={pivotMode}
                        onPivotModeChange={pivotActions.setPivotMode}
                        pivotOrientation={pivotOrientation}
                        onPivotOrientationChange={pivotActions.setPivotOrientation}
                        searchQuery={searchQuery}
                        onSearchQueryChange={pivotActions.setSearchQuery}
                        searchMode={searchMode}
                        onSearchModeChange={pivotActions.setSearchMode}
                        filterMode={filterMode}
                        onFilterModeChange={pivotActions.setFilterMode}
                        flowFilter={flowFilter}
                        onFlowFilterChange={pivotActions.setFlowFilter}
                        actions={
                            // Wraps for the same reason FilterBar's rows do: this
                            // group moves as one unit when the row wraps, so
                            // without its own wrap its tail (Export) overflows
                            // off-screen on narrow viewports.
                            <HStack spacing={2} rowGap={2} flexWrap="wrap">
                                <Button
                                    leftIcon={<RepeatIcon />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={resetSnapshot}
                                    isLoading={snapshotLoading}
                                >
                                    Reset to Original
                                </Button>
                                <Button
                                    leftIcon={<Icon as={MdAutoAwesome} boxSize={4} />}
                                    size="sm"
                                    colorScheme="purple"
                                    onClick={() => aiOpenRef.current?.()}
                                >
                                    AI Analysis
                                </Button>
                                <Button
                                    leftIcon={<Icon as={MdFileDownload} boxSize={4} color="#06B6D4" />}
                                    size="sm"
                                    variant="outline"
                                    color="#06B6D4"
                                    borderColor="#06B6D4"
                                    _hover={{ bg: 'rgba(6, 182, 212, 0.08)' }}
                                    onClick={() => exportRef.current?.()}
                                >
                                    Export
                                </Button>
                            </HStack>
                        }
                        secondaryExtra={
                            <HStack spacing={2} flexShrink={0}>
                            <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" flexShrink={0}>
                                Status
                            </Text>
                            <ButtonGroup size="sm" isAttached variant="outline" flexShrink={0}>
                                <Button
                                    colorScheme={editFilter === 'all' ? 'blue' : 'gray'}
                                    variant={editFilter === 'all' ? 'solid' : 'outline'}
                                    onClick={() => setEditFilter('all')}
                                    fontSize="xs"
                                >
                                    All
                                </Button>
                                <Button
                                    colorScheme={editFilter === 'edited' ? 'orange' : 'gray'}
                                    variant={editFilter === 'edited' ? 'solid' : 'outline'}
                                    onClick={() => setEditFilter('edited')}
                                    fontSize="xs"
                                >
                                    Edited {editedCount > 0 && `(${editedCount})`}
                                </Button>
                                <Button
                                    colorScheme="gray"
                                    variant={editFilter === 'original' ? 'solid' : 'outline'}
                                    onClick={() => setEditFilter('original')}
                                    fontSize="xs"
                                >
                                    Original {originalCount > 0 && `(${originalCount})`}
                                </Button>
                            </ButtonGroup>
                            </HStack>
                        }
                    />

                    {/* Bulk-edit panel — sibling of FilterBar so it stays visible
                        above the scrolling table area, never inside it. */}
                    {bulkEditPanel.visible && (
                        <Card variant="solid" bg="orange.500" color="white" flexShrink={0} borderRadius={0}>
                            <CardBody py={2}>
                                <HStack justify="space-between">
                                    <HStack>
                                        <CheckIcon boxSize={4} />
                                        <Text fontWeight="bold">{bulkEditPanel.count} selected</Text>
                                    </HStack>
                                    <HStack>
                                        <Button
                                            size="sm"
                                            bg="white"
                                            color="orange.600"
                                            leftIcon={<EditIcon />}
                                            onClick={() => bulkEditOpenRef.current?.()}
                                            _hover={{ bg: 'gray.100' }}
                                        >
                                            Bulk Edit
                                        </Button>
                                        <IconButton
                                            aria-label="Clear selection"
                                            icon={<CloseIcon />}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="whiteAlpha"
                                            onClick={() => clearSelectionRef.current?.()}
                                        />
                                    </HStack>
                                </HStack>
                            </CardBody>
                        </Card>
                    )}

                    <Box
                        flex="1"
                        minH={0}
                        display="flex"
                        flexDirection="column"
                        overflow={isTableView ? 'hidden' : 'auto'}
                        px={2}
                        py={3}
                    >
                        <TransactionsExplorer
                            tenantId={selectedTenant?.id}
                            forcedDataSourceMode="EDITABLE"
                            key={`scenario-v2-${selectedTenant?.id}-${explorerResetKey}`}
                            hideControls
                            showEditableUtilityBarWhenControlsHidden
                            controlledStartDate={startDate}
                            controlledEndDate={endDate}
                            controlledViewMode={explorerViewMode}
                            controlledGroupBy={groupBy}
                            accountIds={accountIds}
                            onAccountIdsChange={handleAccountsChange}
                            filterMode={filterMode}
                            controlledFlowFilter={flowFilter}
                            controlledSearchQuery={searchQuery}
                            controlledSearchMode={searchMode}
                            pivotOrientation={pivotOrientation}
                            hideFlowView={true}
                            onAiOpenRef={aiOpenRef}
                            onExportRef={exportRef}
                            onDataLoadingChange={setIsExplorerLoading}
                            bulkEditOpenRef={bulkEditOpenRef}
                            clearSelectionRef={clearSelectionRef}
                            onBulkEditPanelChange={setBulkEditPanel}
                            editFilter={editFilter}
                            onEditFilterChange={setEditFilter}
                            containedScroll={isTableView}
                        />
                    </Box>
                </>
            ) : (
                <Box flex="1" minH={0} overflow="auto" p={6}>
                    <EmptyStateMessage onCreateSnapshot={initSnapshot} isLoading={snapshotLoading} />
                </Box>
            )}

        </Box>
    );
};

const EmptyStateMessage = ({ onCreateSnapshot, isLoading }: { onCreateSnapshot?: () => void; isLoading?: boolean }) => (
    <Box
        p={10}
        textAlign="center"
        borderWidth="1px"
        borderStyle="dashed"
        borderRadius="lg"
        borderColor="xacto.borderGray"
        bg="bg.surface"
    >
        <Heading size="md" mb={2} color="text.primary">No Active Scenario</Heading>
        <Text color="text.secondary" mb={8} maxW="500px" mx="auto">
            Create a safe copy of your transactions to recategorize, rename merchants, and generate clean reports.
        </Text>

        <SimpleGrid columns={3} spacing={6} maxW="600px" mx="auto" mb={8}>
            <VStack spacing={2}>
                <Flex
                    w={12} h={12} borderRadius="full"
                    bg="accent.primary" color="bg.primary"
                    align="center" justify="center"
                >
                    <Icon as={MdContentCopy} boxSize={6} />
                </Flex>
                <Text fontSize="sm" fontWeight="bold" color="text.primary">1. Clone</Text>
                <Text fontSize="xs" color="text.muted">Copy your transaction data</Text>
            </VStack>
            <VStack spacing={2}>
                <Flex
                    w={12} h={12} borderRadius="full"
                    bg="accent.primary" color="bg.primary"
                    align="center" justify="center"
                >
                    <Icon as={MdEdit} boxSize={6} />
                </Flex>
                <Text fontSize="sm" fontWeight="bold" color="text.primary">2. Edit</Text>
                <Text fontSize="xs" color="text.muted">Adjust categories & merchants</Text>
            </VStack>
            <VStack spacing={2}>
                <Flex
                    w={12} h={12} borderRadius="full"
                    bg="accent.primary" color="bg.primary"
                    align="center" justify="center"
                >
                    <Icon as={MdFileDownload} boxSize={6} />
                </Flex>
                <Text fontSize="sm" fontWeight="bold" color="text.primary">3. Export</Text>
                <Text fontSize="xs" color="text.muted">Download a clean report</Text>
            </VStack>
        </SimpleGrid>

        <HStack spacing={2} justify="center" mb={4}>
            <Icon as={MdLock} color="text.muted" boxSize={4} />
            <Text fontSize="xs" color="text.muted">Your original data will NOT be modified</Text>
        </HStack>

        {onCreateSnapshot && (
            <Button
                leftIcon={<AddIcon />}
                colorScheme="xacto.electricCyan"
                variant="solid"
                size="lg"
                onClick={onCreateSnapshot}
                isLoading={isLoading}
            >
                Create Work Scenario
            </Button>
        )}
    </Box>
);
